<template>
  <div class="tw-flex tw-items-center tw-gap-4">
    <SfButtonDs
      v-tooltip.placementBottom="toggleLabel"
      icon-only
      icon-before="chart-line"
      variant="secondary"
      data-testid="left-drawer-report-graphs-toggle"
      :aria-label="toggleLabel"
      :aria-pressed="store.isOpen"
      @click="store.toggleDrawer"
    />
    <label
      v-if="!readOnly && showPinControl"
      v-tooltip.placementBottom="pinToTopTooltip"
      class="
        tw-m-0
        tw-inline-flex
        tw-items-center
        tw-gap-2
        tw-text-sm
        tw-text-sf-neutral-dark-1
      "
      :class="{ 'tw-cursor-pointer': !pinToTopDisabled }"
      data-testid="report-graphs-drawer-pin-to-top"
    >
      <SfToggle
        :model-value="store.pinToTop"
        :icons="false"
        :disabled="pinToTopDisabled"
        :aria-label="pinToTopLabel"
        @update:model-value="onPinToTopChange"
      />
      {{ pinToTopLabel }}
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()
const store = useReportGraphsDrawer()

// Presentation mode is read-only, so the pin control stays hidden there.
const readOnly = window.Silverfin.presentationMode ?? false

const toggleLabel = computed(() =>
  store.isOpen
    ? I18n.t('report.drawer.collapse')
    : I18n.t('report.drawer.expand')
)

// Custom (unlocked) reports show a disabled control; reconciliation reports get none.
const showPinControl = computed(
  () => store.canCustomise && (store.pinnable || !store.locked)
)

// pinLocked: pinned by client-type membership, so the toggle is display-only.
const pinToTopDisabled = computed(() => store.pinLocked || !store.pinnable)

const pinToTopLabel = I18n.t('report.drawer.pin_to_top.label')

const pinToTopTooltip = computed(() => {
  if (store.pinLocked)
    return I18n.t('report.drawer.pin_to_top.client_type_cannot_unpin')
  if (!store.pinnable)
    return I18n.t('report.drawer.pin_to_top.custom_cannot_pin')
  return I18n.t('report.drawer.pin_to_top.tooltip')
})

function onPinToTopChange(value: boolean): void {
  void store.setPinToTop(value)
}
</script>
